import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { parseScopedQuery } from './searchScope';
import {
  SEARCH,
  TYPE_META,
  highlightTokens,
  matchKind,
  whyFor,
  type Hit,
  type SearchData,
} from './searchShared';

/**
 * One search, two surfaces — the ⌘K palette and `/search` read from this hook so
 * a scope prefix, a type count or the withheld number can never mean two things.
 *
 * The caller owns `value` and `tab` (the palette holds them in state, `/search`
 * holds them in the URL); everything else here is DERIVED per render and never
 * stored, which is what stops the counts drifting out of step with the hits.
 */

/** Terms shorter than this return nothing and hold the palette in its idle state. */
export const MIN_TERM = 2;
/** Handoff: "debounce ~150 ms and cancel in-flight requests". */
const DEBOUNCE_MS = 150;
/** Max rows a group shows before it offers "Show all N →". */
export const PER_GROUP = 4;

export interface SearchGroup {
  type: string;
  label: string;
  icon: string;
  color: string;
  /** Rows to render — capped at PER_GROUP unless this type is the active tab. */
  items: Hit[];
  /** TRUE total for the type, from typeTotal. */
  total: number;
  /** total > what we're showing. */
  overflow: boolean;
}

export interface GlobalSearch {
  /** Prefix-stripped search term. */
  term: string;
  /** Canonical entity type from a `secret:` style prefix, else null. */
  scope: string | null;
  /** What the user actually typed as the prefix, for the chip label. */
  alias: string | null;
  idle: boolean;
  loading: boolean;
  error?: Error;
  hits: Hit[];
  withheld: number;
  /** Highlight fragments for <Highlighted>. */
  marks: string[];
  /** Per-type TRUE totals, keyed by entity type. Types with no hits are absent. */
  counts: Record<string, number>;
  /** Sum of the per-type totals — what "See all N results" should say. */
  total: number;
  /** Totals for the active tab only. */
  shownTotal: number;
  /** Where the match landed, for the "Matched in" facet. `similar` = matched only
   *  by trigram similarity, with the term appearing nowhere in the row. */
  fieldCounts: { name: number; body: number; context: number; similar: number };
  /** Tab-filtered, grouped, capped. Empty groups omitted. */
  groups: SearchGroup[];
  /** Tab-filtered flat list, rank-ordered — the results page renders this. */
  flat: Hit[];
  /**
   * The term the CURRENT hits actually describe, or null while they don't.
   *
   * Needed because there is a window — after `value` changes, before the debounce
   * fires — where nothing is loading yet and the counts still belong to the
   * PREVIOUS term. Anything that pairs a query with its result count (the recents
   * store) has to wait for this, or it records the new query against the old
   * number: typing `zzzqqq` after `weather` logged "zzzqqq — 23 hits".
   */
  settledTerm: string | null;
  refetch: () => void;
}

export function useGlobalSearch({
  value,
  tab = 'all',
  limitPerType,
  skip = false,
}: {
  value: string;
  tab?: string;
  limitPerType: number;
  skip?: boolean;
}): GlobalSearch {
  const { scope, alias, term } = parseScopedQuery(value);
  const idle = term.length < MIN_TERM;

  // Debounce the TERM, not the query call: the request then rides Apollo's normal
  // lifecycle, so switching scope or unmounting cancels it for free rather than
  // needing a manual abort.
  const [debounced, setDebounced] = useState(term);
  useEffect(() => {
    if (term.length < MIN_TERM) {
      setDebounced(term);
      return;
    }
    const t = setTimeout(() => setDebounced(term), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term]);

  const { data, previousData, loading, error, refetch } = useQuery<SearchData>(SEARCH, {
    variables: { q: debounced, limitPerType, entityType: scope },
    skip: skip || debounced.length < MIN_TERM,
    fetchPolicy: 'cache-and-network',
  });

  // Keep the previous result set on screen while the next one loads. Clearing to a
  // spinner makes the list jump on every keystroke; the surfaces dim it instead.
  const live = data ?? previousData;
  const hits = useMemo(() => (idle ? [] : (live?.search ?? [])), [idle, live]);
  const withheld = idle ? 0 : (live?.searchWithheld ?? 0);

  // Highlight against the LIVE term, not the debounced one, so marks track typing.
  // For ~150ms these can be a keystroke ahead of the hits — a token then simply
  // finds no match and the row renders plain, the same graceful path as a fuzzy hit.
  const marks = useMemo(() => highlightTokens(term), [term]);

  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    // typeTotal is identical on every row of a type, so last-write-wins is fine.
    for (const h of hits) out[h.entityType] = h.typeTotal;
    return out;
  }, [hits]);

  const total = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);

  // One classifier, shared with the row chips (matchKind) — counting here with a
  // second copy of the rules is how a facet ends up disagreeing with the rows it
  // claims to describe.
  const fieldCounts = useMemo(() => {
    const out = { name: 0, body: 0, context: 0, similar: 0 };
    for (const h of hits) out[matchKind(h, term)] += 1;
    return out;
  }, [hits, term]);

  const tabbed = useMemo(
    () => (tab === 'all' ? hits : hits.filter((h) => h.entityType === tab)),
    [hits, tab],
  );

  const groups = useMemo<SearchGroup[]>(
    () =>
      TYPE_META.map((m) => {
        const all = tabbed.filter((h) => h.entityType === m.type);
        // A tabbed-into group shows everything it has; in "all types" each group is
        // a preview capped at PER_GROUP.
        const items = tab === m.type ? all : all.slice(0, PER_GROUP);
        const totalForType = counts[m.type] ?? all.length;
        return {
          type: m.type,
          label: m.label,
          icon: m.icon,
          color: m.color,
          items,
          total: totalForType,
          overflow: totalForType > items.length,
        };
      }).filter((g) => g.items.length > 0),
    [tabbed, tab, counts],
  );

  return {
    term,
    scope,
    alias,
    idle,
    loading,
    error: error as Error | undefined,
    hits,
    withheld,
    marks,
    counts,
    total,
    shownTotal: tab === 'all' ? total : (counts[tab] ?? 0),
    fieldCounts,
    groups,
    flat: tabbed,
    // `data` (not `live`) on purpose: previousData is the stale set we deliberately
    // keep on screen, and it must never be mistaken for a settled answer.
    settledTerm: !loading && !error && data && debounced === term && !idle ? term : null,
    refetch: () => void refetch(),
  };
}

/** Re-exported so callers don't need a second import for the chip helper. */
export { whyFor };
